import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';

const SESSION_ID_HEADER: string = 'X-Explain-Session-Id';
const SESSION_ID_STORAGE_KEY: string = 'explain-session-id';

/**
 * Attaches a per-browsing-session identifier to every API request so the
 * backend (SessionTimeTrackingMiddleware) can record how long users spend on
 * the platform.
 *
 * The id is generated once per tab session and kept in sessionStorage, so it
 * survives in-app navigation and page refreshes but a new tab/window (or
 * closing and reopening the browser) starts a new session.
 */
@Injectable()
export class SessionIdInterceptor implements HttpInterceptor {

    // In-memory fallback so the id stays stable for the app lifetime even if
    // sessionStorage is unavailable.
    private inMemorySessionId: string | null = null;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url.startsWith(environment.apiUrl)) {
            request = request.clone({
                setHeaders: {[SESSION_ID_HEADER]: this.getSessionId()}
            });
        }
        return next.handle(request);
    }

    private getSessionId(): string {
        if (this.inMemorySessionId) {
            return this.inMemorySessionId;
        }
        try {
            let sessionId: string | null = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
            if (!sessionId) {
                sessionId = this.generateSessionId();
                sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
            }
            this.inMemorySessionId = sessionId;
            return sessionId;
        } catch {
            // sessionStorage unavailable (e.g. blocked); keep a stable id in
            // memory for this app instance instead.
            this.inMemorySessionId = this.generateSessionId();
            return this.inMemorySessionId;
        }
    }

    private generateSessionId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        // Fallback for older browsers without crypto.randomUUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string): string => {
            const r: number = Math.random() * 16 | 0;
            const v: number = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}
